import { useQueryClient } from '@tanstack/react-query';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile } from '~/ui/core/tile/Tile';

import {
    CompanyInformationContent,
    jobArchitectureKeys,
    OverviewContent,
    UploadDataContent,
} from '../../job-architecture-v2';
import type { TaskPanelContext } from '../task-panel-types';

export const SetupTaskPanel: FunctionComponent<TaskPanelContext> = ({
    jobArchitectureId,
    currentTaskId,
    selectTask,
    clearTask,
}) => {
    const translate = useTranslate();
    const queryClient = useQueryClient();

    const invalidateOnboardingState = useCallback((): void => {
        queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.onboardingState(jobArchitectureId) });
    }, [jobArchitectureId, queryClient]);

    const handleCompanyInformationContinue = useCallback((): void => {
        invalidateOnboardingState();
        selectTask('data-upload');
    }, [invalidateOnboardingState, selectTask]);

    const handleComplete = useCallback((): void => {
        clearTask();
        invalidateOnboardingState();
        queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.architectureDetail(jobArchitectureId) });
        queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.metadata(jobArchitectureId) });
    }, [clearTask, invalidateOnboardingState, jobArchitectureId, queryClient]);

    return (
        <Tile>
            <div className="flex h-full w-full flex-col font-sans [&_button]:font-[inherit] [&_input]:font-[inherit] [&_label]:font-[inherit] [&_textarea]:font-[inherit]">
                <div className="flex flex-row items-center justify-between gap-m border-b border-gray-300 px-3xl py-xl">
                    <Button
                        aria-label={translate('common.close')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={clearTask}
                    >
                        <IconCrossSmall />
                    </Button>
                </div>
                <div className="flex flex-1 flex-col overflow-hidden [&_[data-radix-scroll-area-viewport]]:scroll-pb-[6.5rem] [&_[data-radix-scroll-area-viewport]>div]:!flex [&_[data-radix-scroll-area-viewport]>div]:flex-col [&_[data-radix-scroll-area-viewport]>div]:min-h-full">
                    <div className="flex-1 overflow-hidden">
                        <ScrollArea orientation="vertical" type="hover">
                            {currentTaskId === 'define-purpose' && (
                                <OverviewContent
                                    jobArchitectureId={jobArchitectureId}
                                    onContinue={() => selectTask('company-information')}
                                />
                            )}
                            {currentTaskId === 'company-information' && (
                                <CompanyInformationContent
                                    jobArchitectureId={jobArchitectureId}
                                    onContinue={handleCompanyInformationContinue}
                                />
                            )}
                            {currentTaskId === 'data-upload' && (
                                <UploadDataContent jobArchitectureId={jobArchitectureId} onComplete={handleComplete} />
                            )}
                        </ScrollArea>
                    </div>
                </div>
            </div>
        </Tile>
    );
};
